import React, { useEffect, useState } from 'react';
import { Descope } from '@descope/react-sdk';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { logger } from '../utils/logger';

/**
 * Registration flow:
 * 1. Display the Descope hosted/embedded sign-up & login flow (no local email/password inputs).
 * 2. Once the user successfully authenticates we redirect them to the role-selection screen
 *    (/user-type-selection) where they decide whether they are a Candidate or Interviewer.
 *
 * NOTE: Organization Admin accounts are provisioned separately; Interviewers join an organisation
 *       via invitation and must supply an Organisation ID if they register without an invite.
 */
const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Get invitation parameters from URL
  const invitationToken = searchParams.get('token');
  const invitationEmail = searchParams.get('email');
  const invitationRole = searchParams.get('role');
  const organizationId = searchParams.get('organizationId');
  const organizationName = searchParams.get('organizationName');

  /**
   * If the user is already authenticated we can skip the Descope widget and move straight to
   * the role-selection step.
   */
  useEffect(() => {
    if (isAuthenticated) {
      if (invitationToken) {
        // If they have an invitation, redirect to accept it
        navigate(`/invitation?token=${invitationToken}`);
      } else {
        navigate('/user-type-selection');
      }
    }
  }, [isAuthenticated, navigate, invitationToken]);

  /**
   * Callback when Descope authentication succeeds.
   */
  const handleSuccess = async () => {
    logger.info('User successfully registered / logged-in via Descope');
    
    if (invitationToken) {
      // If registering via invitation, store the invitation details
      sessionStorage.setItem('pendingInvitation', JSON.stringify({
        token: invitationToken,
        role: invitationRole,
        organizationId,
        organizationName
      }));
      
      // Redirect to invitation acceptance page
      navigate(`/invitation?token=${invitationToken}`);
    } else {
      navigate('/user-type-selection');
    }
  };

  /**
   * Callback when Descope authentication fails.
   */
  const handleError = (e: unknown) => {
    logger.error('Descope registration error', e);
    setError('There was a problem creating your account. Please try again.');
  };

  // Build Descope customizations
  const descopeCustomization = invitationEmail ? {
    form: {
      email: invitationEmail
    }
  } : undefined;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {invitationToken ? 'Create your account to accept invitation' : 'Create your SkillFoundry account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {invitationToken ? (
              <>
                You've been invited to join <strong>{organizationName}</strong> as a <strong>{invitationRole}</strong>
              </>
            ) : (
              'Secure authentication is handled by Descope'
            )}
          </p>
          {invitationEmail && (
            <p className="mt-2 text-center text-sm text-gray-500">
              Please register with email: <strong>{invitationEmail}</strong>
            </p>
          )}
        </div>

        {/* Descope sign-up / sign-in component */}
        <Descope 
          flowId="sign-up-or-in" 
          onSuccess={handleSuccess} 
          onError={handleError}
          {...(descopeCustomization && { customization: descopeCustomization })}
        />

        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register; 